<script setup lang="ts">
const runtimeConfig = useRuntimeConfig()
const route = useRoute()

const { data: article } = await useAsyncData(route.path, () =>
  queryCollection('articles').path(route.path.toLowerCase()).first(),
)

const { data: allArticles } = await useAsyncData('all-articles-for-related', () =>
  queryCollection('articles').order('order', 'ASC').all(),
)

const MAX_RELATED = 4

const relatedArticles = computed(() => {
  if (!article.value || !allArticles.value) return []
  const currentPath = article.value.path
  const seen = new Set<string>([currentPath])
  const result: typeof allArticles.value = []

  // 1. 手動指定の related（slug で指定）
  if (article.value.related) {
    for (const slug of article.value.related) {
      const found = allArticles.value.find(a => a.path === `/articles/${slug}`)
      if (found && !seen.has(found.path) && !found.draft) {
        result.push(found)
        seen.add(found.path)
      }
    }
  }

  // 2. 同カテゴリの記事で埋める
  if (result.length < MAX_RELATED) {
    for (const a of allArticles.value) {
      if (result.length >= MAX_RELATED) break
      if (!seen.has(a.path) && a.category === article.value!.category && !a.draft) {
        result.push(a)
        seen.add(a.path)
      }
    }
  }

  return result
})

useSeoMeta({
  title: article.value ? `${article.value.title} | Code & Magic` : 'Code & Magic',
  description: article.value?.description || '',
  ogTitle: article.value ? `${article.value.title} | Code & Magic` : 'Code & Magic',
  ogDescription: article.value?.description || '',
  ogImage: article.value?.image
    ? `${runtimeConfig.public.serviceDomain}${article.value.image}`
    : `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  ogType: 'article',
  twitterCard: 'summary_large_image',
})

useHead({
  link: [
    { rel: 'alternate', type: 'application/rss+xml', title: 'エンコーディア通信 RSS', href: '/feed.xml' },
    { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
    { rel: 'preconnect', href: 'https://fonts.gstatic.com', crossorigin: '' },
    { rel: 'stylesheet', href: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700;800&display=swap' },
  ],
})

// JSON-LD: Article
if (article.value) {
  useJsonLd({
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': article.value.title,
    'description': article.value.description,
    'datePublished': article.value.date,
    'image': article.value.image
      ? `${runtimeConfig.public.serviceDomain}${article.value.image}`
      : `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
    'publisher': {
      '@type': 'Organization',
      'name': 'サムライアプス株式会社',
      'url': 'https://www.samurai-apps.com/',
    },
  })
}

definePageMeta({
  layout: 'simple-landing',
})

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`
}
</script>

<template>
  <div class="article-bg">
    <div class="centered-area">
      <div class="rounded-area">
        <NuxtLink
          to="/"
          class="home-logo"
        >
          <BrandTextLogo />
        </NuxtLink>

        <template v-if="article">
          <header class="article-header">
            <span class="article-category">{{ article.category }}</span>
            <h1 class="article-headline">
              {{ article.title }}
            </h1>
            <time class="article-date">{{ formatDate(article.date) }}</time>
          </header>

          <div class="article-card">
            <img
              v-if="article.image"
              :src="article.image"
              :alt="article.imageAlt || article.title"
              class="article-hero-image"
            >
            <article class="article-body">
              <ContentRenderer :value="article" />
            </article>
          </div>
        </template>

        <template v-else>
          <div class="not-found">
            <h1>記事が見つかりません</h1>
            <p>お探しの記事は存在しないか、移動された可能性があります。</p>
          </div>
        </template>

        <div
          v-if="relatedArticles.length > 0"
          class="related-section"
        >
          <h2 class="related-heading">
            関連記事
          </h2>
          <div class="related-list">
            <NuxtLink
              v-for="rel in relatedArticles"
              :key="rel.path"
              :to="rel.path"
              class="related-card"
            >
              <span class="related-category">{{ rel.category }}</span>
              <span class="related-title">{{ rel.title }}</span>
            </NuxtLink>
          </div>
        </div>

        <nav class="article-nav">
          <NuxtLink
            to="/articles"
            class="nav-link"
          >
            記事一覧に戻る
          </NuxtLink>
          <NuxtLink
            to="/"
            class="nav-link"
          >
            ホームに戻る
          </NuxtLink>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import '~/assets/css/landing-variables.css';

.article-bg {
  font-family: "Noto Serif JP", "Noto Sans JP", serif;
  color: var(--landing-color-text);
  width: 100%;
  max-width: 100%;
  padding: 0;
}

.centered-area {
  width: 100%;
  max-width: var(--landing-max-width);
  margin: auto;
  padding: var(--landing-gutter);
  padding-top: 2rem;
  padding-bottom: 2rem;
}

.rounded-area {
  margin: 0 0.5em;
  padding: var(--landing-rounded-padding);
  background-color: var(--landing-color-card-warm);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow);
}

/* ---- Home logo ---- */
.home-logo {
  display: block;
  margin-top: -0.3rem;
  margin-bottom: 0;
}

.home-logo :deep(.shadowed) {
  height: 2.5rem;
  filter: none;
  opacity: 0.5;
  transition: opacity 0.2s;
}

.home-logo:hover :deep(.shadowed) {
  opacity: 0.8;
}

.article-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid var(--landing-color-border-mint);
}

.article-category {
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--landing-color-teal-dark);
  background-color: var(--landing-color-card-mint);
  padding: 0.15rem 0.6rem;
  border-radius: 4px;
  margin-bottom: 0.75rem;
}

.article-headline {
  color: var(--landing-color-navy);
  font-size: 1.8rem;
  font-weight: 900;
  line-height: 1.5;
  margin-bottom: 0.5rem;
}

.article-date {
  font-size: 0.85rem;
  color: #999;
}

.article-card {
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  overflow: hidden;
}

.article-hero-image {
  width: 100%;
  max-height: 320px;
  object-fit: cover;
  display: block;
}

.article-body {
  padding: 1.5rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.7;
}

.article-body :deep(h1) {
  display: none;
}

.article-body :deep(h2) {
  font-size: 1.25rem;
  margin-top: 2rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.article-body :deep(h2 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.article-body :deep(h3) {
  font-size: 1.1rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
}

.article-body :deep(h3 a) {
  color: var(--landing-color-text);
  text-decoration: none;
}

.article-body :deep(p) {
  margin-bottom: 0.85rem;
}

.article-body :deep(ul),
.article-body :deep(ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

.article-body :deep(li) {
  margin-bottom: 0.3rem;
}

.article-body :deep(strong) {
  color: var(--landing-color-navy);
  font-weight: 900;
}

.article-body :deep(pre) {
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  background: rgba(36, 52, 71, 0.06);
  border-radius: 4px;
  overflow-x: auto;
}

/* Markdown テーブル: 既存 landing パレット (mint header / 角丸 / zebra) に
   合わせて少しだけ装飾。記事内に code span が多いので、code 用のスタイルも
   テーブル文脈で微調整。 */
.article-body :deep(table) {
  width: 100%;
  margin: 1rem 0 1.25rem;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid var(--landing-color-border-mint);
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  overflow: hidden;
  font-size: 0.95rem;
}

.article-body :deep(thead) {
  background: var(--landing-color-card-mint);
}

.article-body :deep(th) {
  text-align: left;
  padding: 0.55rem 0.9rem;
  font-weight: 700;
  color: var(--landing-color-navy);
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.article-body :deep(td) {
  padding: 0.5rem 0.9rem;
  border-bottom: 1px solid var(--landing-color-border-mint);
  vertical-align: top;
  word-break: break-word;
}

.article-body :deep(tbody tr:last-child td) {
  border-bottom: none;
}

.article-body :deep(tbody tr:nth-child(even) td) {
  background: rgba(214, 229, 225, 0.22); /* mint tinted zebra */
}

.article-body :deep(tbody tr:hover td) {
  background: rgba(214, 229, 225, 0.4);
}

.article-body :deep(table code) {
  background: rgba(36, 52, 71, 0.06);
  color: var(--landing-color-navy);
  padding: 0.1em 0.4em;
  border-radius: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
  white-space: nowrap;
}

/* インライン説明用の小アイコン（メディアアセット記事のファイル種別表など）は
   本文画像の大きなサイズにならないよう、テーブル内 img を em ベースで小さく。 */
.article-body :deep(td img) {
  width: 1.4em;
  height: 1.4em;
  vertical-align: middle;
  display: inline-block;
}

.related-section {
  margin-top: 2rem;
}

.related-heading {
  font-size: 1.1rem;
  font-weight: 900;
  color: var(--landing-color-navy);
  margin-bottom: 0.8rem;
  padding-bottom: 0.3rem;
  border-bottom: 2px solid var(--landing-color-border-mint);
}

.related-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.related-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #fff;
  border-radius: var(--landing-radius);
  box-shadow: var(--landing-shadow-light);
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--landing-color-text);
  transition: box-shadow 0.2s, transform 0.2s;
}

.related-card:hover {
  box-shadow: var(--landing-shadow);
  transform: translateY(-1px);
}

.related-category {
  flex-shrink: 0;
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--landing-color-teal-dark);
  background-color: var(--landing-color-card-mint);
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
}

.related-title {
  font-size: 0.9rem;
  font-weight: 700;
  line-height: 1.4;
}

.not-found {
  text-align: center;
  padding: 4rem 1rem;
}

.article-nav {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--landing-color-border-mint);
}

.nav-link {
  color: var(--landing-color-teal-dark);
  text-decoration: none;
  font-size: 0.85rem;
}

.nav-link:hover {
  text-decoration: underline;
}
</style>
